import { emitPaywall, paywallPayloadFromBody } from "@/lib/paywall";

export const BASE = import.meta.env.BASE_URL;
export const API_BASE = `${BASE}api`;

// Inspect a `fetch` Response for the
// `402 { code: "entitlement_required", slug, paywallEnabled }` paywall
// contract and, if matched, publish a paywall event so the global
// PaywallModal can render. Uses `response.clone()` so the caller can
// still consume the body normally. Safe to call on every response.
export async function maybeEmitPaywall(response: Response): Promise<void> {
  if (response.status !== 402) return;
  try {
    const body = (await response.clone().json()) as unknown;
    const payload = paywallPayloadFromBody(body);
    if (payload) emitPaywall(payload);
  } catch {
    // Non-JSON 402 — not an entitlement paywall; ignore.
  }
}

// Thin wrapper around `fetch` that automatically detects 402 paywall
// responses and surfaces them through the global paywall modal. Use
// this for any direct fetch call that hits an entitlement-gated
// endpoint. The returned Response is unconsumed and behaves exactly
// like `fetch()`.
export async function fetchApi(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);
  await maybeEmitPaywall(response);
  return response;
}

export function resolveImageUrl(path: string | null | undefined): string {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/api/")) return path;
  if (path.startsWith("/storage/")) return `${BASE.replace(/\/$/, "")}${path}`;
  // Object-storage paths are stored in the DB as `/objects/uploads/<uuid>`.
  // The single gated server route that resolves them and re-checks ACL +
  // tenant membership is mounted at `/api/storage/objects/*` (Express
  // router in artifacts/api-server/src/routes/storage.ts under the
  // `/api` mount in app.ts). We rewrite to that exact path so a fresh
  // signed URL is minted per fetch and a soft-deleted / cross-tenant
  // object can never leak. This is the Layer-3 contract for the
  // Keepsake Recap v1 photo flow and applies uniformly to every other
  // surface that returns `/objects/...` paths (postcards, finds,
  // hunt-detail, map popups, inbox attachments).
  if (path.startsWith("/objects/")) return `${API_BASE}/storage${path}`;
  return path;
}

export async function uploadHuntPhoto(file: File): Promise<string> {
  const ext = (file.type.split("/")[1] || "jpg").toLowerCase();
  const presignRes = await fetch(`${API_BASE}/storage/uploads/request-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      name: `find-${Date.now()}.${ext}`,
      size: file.size,
      contentType: file.type,
    }),
  });
  if (!presignRes.ok) throw new Error("Could not get upload URL");
  const { uploadURL, objectPath } = (await presignRes.json()) as {
    uploadURL: string;
    objectPath: string;
  };
  const putRes = await fetch(uploadURL, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putRes.ok) throw new Error("Upload failed");
  return objectPath;
}
